
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Lock, Mail, Ticket, ArrowRight, RefreshCcw } from 'lucide-react';

interface AuthModalProps {
  onLogin: (username: string) => void;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ onLogin, onClose }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      if (mode === 'forgot') {
        alert("A recovery ticket has been sent to your email.");
        setMode('login');
      } else {
        onLogin(username || 'Guest Critic');
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-[#3a0303] max-w-md w-full relative rounded-xl border-4 border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.3)] p-8 overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-[#d4af37]/20">
          <motion.div 
            className="h-full bg-[#d4af37]"
            animate={{ x: loading ? ['-100%', '100%'] : '0%' }}
            transition={{ repeat: Infinity, duration: 1 }}
          />
        </div>

        <button onClick={onClose} aria-label="Close Auth Modal" className="absolute top-4 right-4 text-[#d4af37] hover:text-white transition-colors">
          <X size={24} />
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#d4af37] text-[#d4af37] mb-4">
            {mode === 'login' ? <Ticket size={32} /> : mode === 'signup' ? <User size={32} /> : <RefreshCcw size={32} />}
          </div>
          <h2 className="font-marquee text-2xl text-[#d4af37] uppercase tracking-widest">
            {mode === 'login' ? 'Stage Door' : mode === 'signup' ? 'New Member' : 'Recover Ticket'}
          </h2>
          <p className="text-[10px] text-white/40 uppercase tracking-widest mt-2">Optional Account Access</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37]/40" size={18} />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full bg-black/40 border border-[#d4af37]/20 rounded-lg py-3 pl-10 pr-4 text-sm focus:outline-none focus:border-[#d4af37] transition-all"
                required={mode !== 'forgot'}
              />
            </div>

            {mode !== 'forgot' && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37]/40" size={18} />
                <input
                  type="password"
                  placeholder="Password"
                  className="w-full bg-black/40 border border-[#d4af37]/20 rounded-lg py-3 pl-10 pr-4 text-sm focus:outline-none focus:border-[#d4af37] transition-all"
                  required
                />
              </div>
            )}

            {mode === 'signup' && (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37]/40" size={18} />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full bg-black/40 border border-[#d4af37]/20 rounded-lg py-3 pl-10 pr-4 text-sm focus:outline-none focus:border-[#d4af37] transition-all"
                  required
                />
              </div>
            )}
            
            {mode === 'forgot' && (
               <div className="relative">
               <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37]/40" size={18} />
               <input
                 type="email"
                 placeholder="Email Address"
                 className="w-full bg-black/40 border border-[#d4af37]/20 rounded-lg py-3 pl-10 pr-4 text-sm focus:outline-none focus:border-[#d4af37] transition-all"
                 required
               />
             </div>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-[#d4af37] text-black font-bold uppercase py-3 rounded-sm shadow-lg flex items-center justify-center gap-2 group"
          >
            {mode === 'login' ? 'Enter Theater' : mode === 'signup' ? 'Claim Ticket' : 'Send Recovery'}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </form>

        <div className="mt-8 flex flex-col gap-2 text-center">
          {mode === 'login' ? (
            <>
              <button onClick={() => setMode('signup')} className="text-[#d4af37]/60 hover:text-[#d4af37] text-[10px] uppercase tracking-widest transition-colors">Don't have a ticket? Register here</button>
              <button onClick={() => setMode('forgot')} className="text-[#d4af37]/60 hover:text-[#d4af37] text-[10px] uppercase tracking-widest transition-colors">Forgot your seat number?</button>
            </>
          ) : (
            <button onClick={() => setMode('login')} className="text-[#d4af37]/60 hover:text-[#d4af37] text-[10px] uppercase tracking-widest transition-colors">Back to Entrance</button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuthModal;
